/**
 *  @class
 *  @function CartUpsellProducts
 *
 *  Upsell a taglia unica nel cart drawer (snippets/cart-drawer-upsell-free-shipping.liquid).
 *  data-urls contiene le passate in ordine di priorità: ognuna è la sezione cart-drawer
 *  renderizzata su una ricerca filtrata lato server. Si richiedono in sequenza e ci si
 *  ferma appena si raggiunge data-limit, scartando i prodotti e le categorie già presi.
 *
 *  Con il carrello unisex c'è anche data-urls-split: un secondo gruppo di passate (data-urls
 *  è Donna, data-urls-split è Uomo). Le card dei due gruppi vengono alternate una per volta,
 *  così i consigli sono metà e metà.
 */
if (!customElements.get('cart-upsell-products')) {
  class CartUpsellProducts extends HTMLElement {
    constructor() {
      super();

      this.parent = this.closest('.product-recommendations--parent');
    }
    connectedCallback() {
      // Il drawer viene ri-renderizzato a ogni modifica del carrello: ogni istanza carica le sue card
      if (this.dataset.urls) {
        this.fetchProducts();
      }
    }
    // Candidati di un gruppo di passate, in ordine di priorità
    async fetchGroup(urls, limit) {
      const candidates = [];
      const seen = new Set();
      const seenCategories = new Set();
      let heading = null;

      for (const url of urls) {
        if (candidates.length >= limit) {
          break;
        }
        try {
          const response = await fetch(url);
          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');
          const source = html.querySelector('cart-upsell-products');

          if (!source) {
            continue;
          }
          heading = heading || source.querySelector('.cart-drawer--recommendations--heading');

          source.querySelectorAll('product-card').forEach((card) => {
            // Mai due prodotti della stessa categoria (data-category = product.type)
            // né lo stesso prodotto due volte (URL senza parametri)
            const link = card.querySelector('.cart-product-link');
            const productKey = link ? link.getAttribute('href').split('?')[0] : card.innerHTML;
            const categoryKey = card.dataset.category || productKey;

            if (!seen.has(productKey) && !seenCategories.has(categoryKey)) {
              seen.add(productKey);
              seenCategories.add(categoryKey);
              candidates.push({ card, productKey, categoryKey });
            }
          });
        } catch (e) {
          console.error(e);
        }
      }

      return { candidates, heading };
    }
    async fetchProducts() {
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const urlGroups = [JSON.parse(this.dataset.urls)];

      if (this.dataset.urlsSplit) {
        urlGroups.push(JSON.parse(this.dataset.urlsSplit));
      }

      const groups = await Promise.all(urlGroups.map((urls) => this.fetchGroup(urls, limit)));
      const queues = groups.map((group) => group.candidates);
      const headingGroup = groups.find((group) => group.heading);
      const heading = headingGroup ? headingGroup.heading : null;
      const cards = [];
      const seen = new Set();
      const seenCategories = new Set();

      // Una card per gruppo a ogni giro: con un gruppo solo è l'ordine delle passate, con due
      // i generi si alternano. Se un gruppo finisce i candidati, l'altro completa il limite
      while (cards.length < limit && queues.some((queue) => queue.length)) {
        for (const queue of queues) {
          if (cards.length >= limit) {
            break;
          }
          while (queue.length) {
            const candidate = queue.shift();

            if (seen.has(candidate.productKey) || seenCategories.has(candidate.categoryKey)) {
              continue;
            }
            seen.add(candidate.productKey);
            seenCategories.add(candidate.categoryKey);
            cards.push(candidate.card);
            break;
          }
        }
      }

      // Nel frattempo il drawer potrebbe essere stato ri-renderizzato: questa istanza non è più nel DOM
      if (!this.isConnected || !cards.length || !heading) {
        return;
      }

      const products = document.createElement('div');
      products.className = 'cart-drawer--recommendations--products';
      cards.forEach((card) => products.appendChild(document.importNode(card, true)));

      this.replaceChildren(document.importNode(heading, true), products);
      this.classList.add('product-recommendations--loaded');

      if (this.parent) {
        this.parent.hidden = false;
        this.parent.classList.add('product-recommendations--full');

        if (document.body.classList.contains('open-cart')) {
          // Un frame di ritardo perché la transizione del pannello desktop parta dallo stato chiuso
          requestAnimationFrame(() => this.parent.classList.add('active'));
        }
      }
    }
  }
  customElements.define('cart-upsell-products', CartUpsellProducts);
}
